import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'

export interface ScreeningMockState {
    is_mock: boolean
    status: string
    property_name: string
    prospect_name: string
    message: string
    can_submit: boolean
    submitted: boolean
    redirect_url?: string
}

export interface ScreeningMockSubmitPayload {
    ssn_last4: string
    date_of_birth: string
    monthly_income: string
    consent: boolean
}

export const useScreeningMock = (token: string | undefined) => {
    const queryClient = useQueryClient()

    const stateQuery = useQuery<AxiosResponse<ScreeningMockState>, Error>({
        queryKey: ['screening-mock', token],
        queryFn: () => axiosInstance.get(`/outreach/screening-mock/${token}/`),
        enabled: !!token,
        retry: false,
    })

    const submitMutation = useMutation<
        AxiosResponse<ScreeningMockState>,
        AxiosError<{ error?: string }>,
        ScreeningMockSubmitPayload
    >({
        mutationFn: (payload) =>
            axiosInstance.post(
                `/outreach/screening-mock/${token}/submit/`,
                payload,
            ),
        onSuccess: (response) => {
            queryClient.setQueryData(['screening-mock', token], response)
        },
    })

    const axiosError = stateQuery.error as AxiosError<{ error?: string }> | null
    const errorMessage =
        axiosError?.response?.data?.error ||
        (stateQuery.isError ? 'Unable to load screening form.' : null)

    return {
        state: stateQuery.data?.data ?? null,
        isLoading: stateQuery.isLoading,
        error: errorMessage ? new Error(errorMessage) : null,
        submit: submitMutation.mutateAsync,
        isSubmitting: submitMutation.isLoading,
        submitError:
            submitMutation.error?.response?.data?.error ||
            (submitMutation.isError ? 'Submit failed.' : null),
    }
}
